import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';

export function useDashboardSection<T>(section: 'overview' | 'email' | 'activity') {
  const [state, setState] = useState<{ data?: T; error?: string }>({});
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    setState({});
    void api<T>(`/analytics/dashboard/${section}`, { signal: controller.signal }).then(
      (data) => { if (!controller.signal.aborted) setState({ data }); },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof Response && error.headers.get('Location')) {
          void navigate(error.headers.get('Location')!, { replace: true });
          return;
        }
        setState({ error: 'This section could not be loaded. Please try again.' });
      },
    );
    return () => controller.abort();
  }, [section, attempt, navigate]);

  return { ...state, retry: () => setAttempt((value) => value + 1) };
}

export function Skeleton({ className }: { className: string }) {
  return <div aria-hidden="true" className={`rounded bg-slate-200 motion-safe:animate-pulse ${className}`} />;
}

export function SectionContent<T>({ state, label, fallback, children, showError = true, errorClassName = '' }: {
  state: { data?: T; error?: string; retry: () => void };
  label: string;
  fallback: ReactNode;
  children: (data: T) => ReactNode;
  showError?: boolean;
  errorClassName?: string;
}) {
  if (state.data) return children(state.data);
  if (!state.error) return fallback;
  if (!showError) return null;
  return <div role="alert" className={`rounded-lg border bg-white p-4 text-sm ${errorClassName}`}>
    <p className="font-medium">{label} is unavailable</p>
    <p className="mt-1 text-muted-foreground">{state.error}</p>
    <button type="button" className="ff-link mt-2 cursor-pointer" onClick={state.retry}>Retry {label.toLowerCase()}</button>
  </div>;
}
